import { useContext, useState } from "react";
import { ModalContext } from "../../Components/Modals";
import ModalAddSeasonToBasket, {
  ModalAddSeasonToBasketElementProps,
} from "../../Components/Modals/ModalAddSeasonToBasket/modal-add-season-to-basket";
import ModalAddSeasonToBasketForRights, {
  ModalAddSeasonToBasketForRightsElementProps,
} from "../../Components/Modals/ModalAddSeasonToBasketForRights/modal-add-season-to-basket-for-rights";
import { assetService, basketService } from "../../Core/Api/services";
import { AddItemToBasketRequest } from "../../Core/Api/Types/add-item-to-basket-request";
import { DbAssetObjectInfos } from "../../Core/Api/Types/db-asset-object-infos";
import { ObjectLayer } from "../../Core/Api/Types/object-layer";
import { GetMetadataByTagName } from "../../Core/Utils/asset-object-utils";
import { BasketContext } from "../../Layouts/Dashboard/dashboard";
import {
  AddItem,
  AddItems,
  AddPreconfiguredItemDCPOVToBasket,
  AddPreconfiguredItemDCPReelsToBasket,
  CopyItem,
  LoadProfiles,
  ModifyItem,
  SetPanelMessage,
  StoreInitialState,
} from "../../Redux/Reducers/redux-slice-basket";
import { reduxStore } from "../../Redux/redux-store";
import useCustomSnackbar from "./useCustomSnackbar";
import { ContractAndRightType } from "../../Core/Api/Right/right-api-service";
import { useTranslation } from "react-i18next";
import { DbOrderItemInfos } from "../../Core/Api/Types/db-order-item-infos";
import { BasketItemOptions } from "../../Core/Api/Types/basket-item-options";
import { routePaths } from "../../Router/router";

const useBasketActions = () => {
  // Panel layout ctx
  const { t } = useTranslation();
  const { openBasket, setOpenBasket } = useContext(BasketContext);
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const enqueueSnackbar = useCustomSnackbar();

  const [isBasketLoading, setIsBasketLoading] = useState(false);

  const handleAddToBasket = (request: AddItemToBasketRequest[]) => {
    if (request.length > 0) {
      if (request.length > 1) {
        reduxStore.dispatch(AddItems(request)).then((response) => {
          const error = (response as any).error;
          if (!error) {
            reduxStore.dispatch(SetPanelMessage(`${request.length} objects added successfully to basket!`));
            setOpenBasket(true);
          } else {
            enqueueSnackbar(error.message, "warning");
          }
        });
      } else {
        reduxStore.dispatch(AddItem(request[0])).then((response) => {
          const error = (response as any).error;
          if (!error) {
            reduxStore.dispatch(SetPanelMessage(`1 object added successfully to basket!`));
            setOpenBasket(true);
          } else {
            enqueueSnackbar(error.message, "warning");
          }
        });
      }
    }
  };

  const addContractsToBasket = (contracts: ContractAndRightType[]) => {
    let items: AddItemToBasketRequest[] = [];
    contracts.map((obj) => {
      items.push({
        objectId: obj.titleId,
        episodesNbList:
          obj.titleType === "Season"
            ? Array.from({ length: obj.titleNbEpisodes ?? 0 }, (item, index) => (item = index + 1))
            : [],
        externalContractId: obj.externalId,
      });
    });

    let seasonSelectedNb: number = contracts.filter((x) => x.titleType === "Season").length;
    if (seasonSelectedNb > 0) {
      if (seasonSelectedNb === 1) {
        if (!openModal) {
          return;
        }
        openModal<ModalAddSeasonToBasketForRightsElementProps>(ModalAddSeasonToBasketForRights, {
          data: contracts,
          request: items,
          context: "Simple",
          onConfirm: handleAddToBasket,
        });
      } else {
        if (!openModal) {
          return;
        }
        openModal<ModalAddSeasonToBasketForRightsElementProps>(ModalAddSeasonToBasketForRights, {
          data: contracts,
          request: items,
          context: "Multiple",
          onConfirm: handleAddToBasket,
        });
      }
    } else {
      handleAddToBasket(items);
    }
  };

  const addObjectsToBasket = (objects: DbAssetObjectInfos[]) => {
    let items: AddItemToBasketRequest[] = [];
    objects.map((obj) => {
      items.push({
        objectId: obj.id,
        episodesNbList:
          GetMetadataByTagName(obj, "titleType")?.value === "Season"
            ? Array.from(
                { length: GetMetadataByTagName(obj, "serieSeasonEpisodeTotalNumber")?.value },
                (item, index) => (item = index + 1)
              )
            : [],
        externalContractId: null,
      });
    });
    let seasonSelectedNb: number = objects.filter(
      (x) => x.layer === ObjectLayer.Title && GetMetadataByTagName(x, "titleType")?.value === "Season"
    ).length;

    if (seasonSelectedNb > 0) {
      if (!openModal) {
        return;
      }
      openModal<ModalAddSeasonToBasketElementProps>(ModalAddSeasonToBasket, {
        data: objects,
        request: items,
        onConfirm: handleAddToBasket,
      });
    } else {
      handleAddToBasket(items);
    }
  };

  const addObjectToBasket = (obj: DbAssetObjectInfos) => {
    let item: AddItemToBasketRequest = {
      objectId: obj.id,
      episodesNbList:
        GetMetadataByTagName(obj, "titleType")?.value === "Season"
          ? Array.from(
              { length: GetMetadataByTagName(obj, "serieSeasonEpisodeTotalNumber")?.value },
              (item, index) => (item = index + 1)
            )
          : [],
      externalContractId: null,
    };

    if (GetMetadataByTagName(obj, "titleType")?.value === "Season") {
      console.warn("addItemToBasket:: Season selected");
      if (!openModal) {
        return;
      }
      openModal<ModalAddSeasonToBasketElementProps>(ModalAddSeasonToBasket, {
        data: [obj],
        request: [item],
        onConfirm: handleAddToBasket,
      });
    } else {
      reduxStore.dispatch(AddItem(item)).then((response) => {
        const error = (response as any).error;
        if (!error) {
          reduxStore.dispatch(SetPanelMessage(`${obj.layer} added successfully to basket!`));
          setOpenBasket(true);
        } else {
          enqueueSnackbar(error.message, "warning");
        }
      });
    }
  };

  const addObjectByIdToBasket = (id: number) => {
    let item: AddItemToBasketRequest = {
      objectId: id,
      episodesNbList: null,
      externalContractId: null,
    };

    reduxStore.dispatch(AddItem(item)).then((response) => {
      const error = (response as any).error;
      if (!error) {
        reduxStore.dispatch(SetPanelMessage(`Object #${id} added successfully to basket!`));
        setOpenBasket(true);
      } else {
        enqueueSnackbar(error.message, "warning");
      }
    });
    reduxStore.dispatch(SetPanelMessage(`Object #${id} added successfully to basket!`));

    //enqueueCustomSnackbar(objects.length + " item" + (objects.length > 1 ? "" : "") + " added on basket", "success");
    setOpenBasket(true);
  };

  const addObjectByIdsToBasket = (ids: number[]) => {
    let items: AddItemToBasketRequest[] = [];
    let objects: DbAssetObjectInfos[] = [];

    Promise.all(
      ids.map((id) =>
        assetService.GetObjectLightById(id, 1).then(function (response) {
          if (response.graph) {
            objects.push(response.graph);
            items.push({
              objectId: response.graph.id,
              episodesNbList:
                GetMetadataByTagName(response.graph, "titleType")?.value === "Season"
                  ? Array.from(
                      { length: GetMetadataByTagName(response.graph, "serieSeasonEpisodeTotalNumber")?.value },
                      (item, index) => (item = index + 1)
                    )
                  : [],
              externalContractId: null,
            });
          }
        })
      )
    ).then(() => {
      let seasonSelectedNb: number = objects.filter(
        (x) => GetMetadataByTagName(x, "titleType")?.value === "Season"
      ).length;

      if (seasonSelectedNb > 0) {
        if (!openModal) {
          return;
        }
        openModal<ModalAddSeasonToBasketElementProps>(ModalAddSeasonToBasket, {
          data: objects,
          request: items,
          onConfirm: handleAddToBasket,
        });
      } else {
        handleAddToBasket(items);
      }
    });
  };

  const modifyObjectByIdToBasket = (id: number) => {
    reduxStore.dispatch(ModifyItem(id)).then((response) => {
      let resp = response.payload as any;
      if (resp.success) {
        //enqueueSnackbar(`Item #${id} put back in basket for modification!`, "success");
        reduxStore.dispatch(SetPanelMessage(`Item put back in basket for modification!`));
        setOpenBasket(true);
      } else enqueueSnackbar(resp.message, "warning");
    });
  };

  const copyObjectByIdToBasket = (id: number) => {
    reduxStore.dispatch(CopyItem(id)).then((response) => {
      let resp = response.payload as any;
      if (resp.success) {
        reduxStore.dispatch(SetPanelMessage(`Item copied to basket!`));
        setOpenBasket(true);
      } else enqueueSnackbar(resp.message, "warning");
    });
  };

  const addPreconfiguredItemDCPReelsToBasket = (id: number) => {
    let item: AddItemToBasketRequest = {
      objectId: id,
      episodesNbList: null,
      externalContractId: null,
    };

    reduxStore.dispatch(AddPreconfiguredItemDCPReelsToBasket(item)).then((response) => {
      let resp = response.payload as any;
      if (resp.add.success) {
        enqueueSnackbar(t("alert:success.reelsObjectsAddedToBasket"), "success");
        reduxStore.dispatch(SetPanelMessage(t("alert:success.reelsObjectsAddedToBasket")));
        setOpenBasket(true);
      } else enqueueSnackbar(resp.add.message, "warning");
    });
  };

  const loadProfiles = () => {
    reduxStore.dispatch(LoadProfiles()).then((response) => {
      let resp = response.payload as any;
      if (!resp.success) enqueueSnackbar(resp.message ?? t("common:error"), "warning");
    });
  };

  const addPreconfiguredItemDCPOVToBasket = (id: number) => {
    let item: AddItemToBasketRequest = {
      objectId: id,
      episodesNbList: null,
      externalContractId: null,
    };

    reduxStore.dispatch(AddPreconfiguredItemDCPOVToBasket(item)).then((response) => {
      let resp = response.payload as any;
      if (resp.add.success) {
        enqueueSnackbar(t("alert:success.dcpObjectsAddedToBasket"), "success");
        reduxStore.dispatch(SetPanelMessage(t("alert:success.dcpObjectsAddedToBasket")));
        setOpenBasket(true);
      } else enqueueSnackbar(resp.add.message, "warning");
    });
  };

  const reloadBasketPanel = () => {
    setOpenBasket(true);

    basketService.GetBasketQuickView().then((response) => {
      if (response.success) {
        reduxStore.dispatch(StoreInitialState(response.basket?.items));
        reduxStore.dispatch(SetPanelMessage(t("alert:success.itemAddedToBasketForQuotePurposal")));
      }
    });
  };

  async function addPreconfiguredItemToBasket(item: DbOrderItemInfos) {
    try {
      setIsBasketLoading(true);
      enqueueSnackbar("Adding item to basket...", "primary");

      // Clear basket
      const clearResponse = await basketService.ClearBasket();
      if (!clearResponse.success) {
        enqueueSnackbar(t("alert:error.oopsWithMessage", { message: clearResponse.message }), "warning");
        return;
      }

      const episodeNb = item.product?.episodeNb ? [item.product?.episodeNb] : null;
      // Add item to basket
      const addItemsResponse = await basketService.AddItemToBasket({
        objectId: item.objectId,
        episodesNbList: episodeNb,
        externalContractId: null,
      });

      if (!addItemsResponse.success) {
        enqueueSnackbar(t("alert:error.oopsWithMessage", { message: addItemsResponse.message }), "warning");
        return;
      }

      const newItemId = addItemsResponse.items?.[0].id;

      // define directives
      const directivesResponse = await basketService.SetItemsDirectives({
        directives: [
          {
            itemId: newItemId ?? null,
            productId: item.productId ?? null,
            contentId: item.contentId ?? null,
            broadcasterId: item.broadcasterId ?? null,
            manualTaskTemplateId: item.manualTasksTemplateId ?? null,
            includeOnlyAvailableValues: item.includeOnlyAvailableValues ?? null,
          },
        ],
      });

      if (!directivesResponse.success) {
        enqueueSnackbar(t("alert:error.oopsWithMessage", { message: directivesResponse.message }), "warning");
        return;
      }

      // define options
      const options = new BasketItemOptions({
        itemId: newItemId,
        productWithOptions: item.product,
        manualTasksTemplateId: item.manualTasksTemplateId,
        manualTasksTemplateFormFields: item.manualTasksTemplate?.formFields,
        manualTasksTemplateDisabledTasks: null,
        includeOnlyAvailableValues: item.includeOnlyAvailableValues,
        orderScheduledTime: null,
      });

      const optionsResponse = await basketService.SetItemsOptions({
        options: [options],
      });

      if (!optionsResponse.success) {
        enqueueSnackbar(t("alert:error.oopsWithMessage", { message: optionsResponse.message }), "warning");
        return;
      }

      window.open(routePaths.basket.path, "_blank");
    } catch (error) {
      console.error(error);
    } finally {
      setIsBasketLoading(false);
    }
  }

  return {
    addContractsToBasket,
    addObjectsToBasket,
    addObjectToBasket,
    addObjectByIdToBasket,
    addObjectByIdsToBasket,
    modifyObjectByIdToBasket,
    copyObjectByIdToBasket,
    addPreconfiguredItemDCPReelsToBasket,
    addPreconfiguredItemDCPOVToBasket,
    addPreconfiguredItemToBasket,
    reloadBasketPanel,
    loadProfiles,
    isBasketLoading,
  };
};

export default useBasketActions;
